import { type FC, Fragment, useMemo, useState } from 'react'
import { Container, Form } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import classNames from 'classnames'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId, selectAuthenticatedUser } from '../../../store/auth/authSlice'
import { useGetAllUsersQuery } from '../../../store/user/user'
import { UserAvatar } from '../../../components/UserAvatar/UserAvatar'
import { SegmentedControl } from '../../../components/SegmentedControl/SegmentedControl'
import { InsightsNavTabs } from '../InsightsNavTabs'
import { getInitials } from '../../utils'
import {
  buildScoreRows,
  filterRowsByQuery,
  filterVisibleRoster,
  GROUP_ROW_CAP,
  groupFullRoster,
  MIN_MEETINGS_FOR_SCORE,
  type RosterGroupKey,
  SCORE_BENCHMARK,
  type ScoreRow,
  TOP_PERFORMER_CAP,
} from '../../../components/Insights/scoreRoster'
import { useUserScoresGroupedData } from '../../../components/Insights/useUserScoresData'
import { RANGE_OPTIONS, RANGE_PERIOD, RANGE_PERIOD_TEXT, type RangeKey } from '../../../components/Insights/userScoreRange'
import { Sparkline } from '../../../components/Insights/Sparkline/Sparkline'
import { WhatsCounted } from '../../../components/Insights/WhatsCounted/WhatsCounted'
import { ChangeChip } from '../../../components/Insights/ChangeChip/ChangeChip'
import styles from './UserScoresGroupedPage.module.scss'
import { InfoTooltip } from '../../../shared/insights/components/insightsCommon'
import '../../../shared/insights/components/InsightsDashboardLayout.scss'

const GROUPS: { key: RosterGroupKey; title: string; explanation: (timeRange: RangeKey) => string; headerClass: string }[] = [
  { key: 'belowBenchmark', title: 'Below benchmark', explanation: () => `Under ${SCORE_BENCHMARK}.`, headerClass: 'zocks-text-danger-200' },
  {
    key: 'watchZone',
    title: 'Watch zone',
    explanation: () => `At or above ${SCORE_BENCHMARK}, and slipping.`,
    headerClass: 'zocks-text-warning-text',
  },
  {
    key: 'topPerformers',
    title: 'Top performers',
    explanation: () => `Top ${TOP_PERFORMER_CAP} at or above ${SCORE_BENCHMARK}, not slipping.`,
    headerClass: 'zocks-text-success-200',
  },
  {
    key: 'onTrack',
    title: 'On track',
    explanation: () => `At or above ${SCORE_BENCHMARK}, not slipping, and outside the top ${TOP_PERFORMER_CAP}.`,
    headerClass: 'zocks-text-gray-900',
  },
  {
    key: 'missingData',
    title: 'Low volume',
    // "scored" is the load-bearing word: 40 meetings with 3 scored still belongs here
    explanation: (timeRange) => `Fewer than ${MIN_MEETINGS_FOR_SCORE} scored meetings in the ${RANGE_PERIOD_TEXT[timeRange]}.`,
    headerClass: 'zocks-text-gray-600',
  },
]

const hasName = (row: ScoreRow) => row.name.trim().length > 0 && row.name.trim() !== row.email.trim()

const primaryLabel = (row: ScoreRow) => (hasName(row) ? row.name : row.email)

const DEFAULT_VISIBLE_COUNTS: Record<RosterGroupKey, number> = {
  belowBenchmark: GROUP_ROW_CAP,
  watchZone: GROUP_ROW_CAP,
  topPerformers: GROUP_ROW_CAP,
  onTrack: GROUP_ROW_CAP,
  missingData: GROUP_ROW_CAP,
}

const DEFAULT_COLLAPSED: Record<RosterGroupKey, boolean> = {
  belowBenchmark: false,
  watchZone: false,
  topPerformers: false,
  onTrack: true,
  missingData: true,
}

export const UserScoresGroupedPage: FC = () => {
  const navigate = useNavigate()
  const accountId = useAppSelector(selectAccountId)
  const authenticatedUser = useAppSelector(selectAuthenticatedUser)
  const { data, isFetching: isFetchingUsers } = useGetAllUsersQuery(
    {
      accountId: accountId ?? '',
      identityTypes: ['idp'],
    },
    { skip: !accountId },
  )
  const activeUsers = useMemo(() => (data?.data ?? []).filter((user) => user.status === 'active'), [data])
  const visibleUsers = useMemo(() => filterVisibleRoster(activeUsers, authenticatedUser), [activeUsers, authenticatedUser])

  const [collapsed, setCollapsed] = useState<Record<RosterGroupKey, boolean>>(DEFAULT_COLLAPSED)
  const [visibleCounts, setVisibleCounts] = useState<Record<RosterGroupKey, number>>(DEFAULT_VISIBLE_COUNTS)
  const [timeRange, setTimeRange] = useState<RangeKey>('90d')
  const [search, setSearch] = useState('')
  // Typing (or clearing) the search box must not replay the staggered card entrance
  const [skipEntrance, setSkipEntrance] = useState(false)
  const { scorecards, isFetching: isFetchingScores } = useUserScoresGroupedData(accountId ?? '', timeRange)
  const loading = isFetchingUsers || isFetchingScores
  const searching = search.trim().length > 0

  const fullRoster = useMemo(() => buildScoreRows(activeUsers, scorecards), [activeUsers, scorecards])
  const rows = useMemo(() => buildScoreRows(visibleUsers, scorecards), [visibleUsers, scorecards])

  const filteredRows = useMemo(() => filterRowsByQuery(rows, search), [rows, search])

  const groups = useMemo(() => groupFullRoster(filteredRows, fullRoster), [filteredRows, fullRoster])

  const visibleGroups = GROUPS.filter((group) => groups[group.key].length > 0)

  const openRow = (row: ScoreRow) => {
    // Only groups showing more than the default cap need to carry their row count into the pager queue
    const expandedKeys = Object.entries(visibleCounts)
      .filter(([, count]) => count > GROUP_ROW_CAP)
      .map(([key, count]) => `${key}:${count}`)
      .join(',')
    // Collapsed groups render zero rows on screen, so the pager queue must exclude them too
    const visibleKeys = GROUPS.filter((group) => searching || !collapsed[group.key])
      .map((group) => group.key)
      .join(',')
    navigate(
      `/insights/coaching/${row.id}?range=${timeRange}&view=grouped&visible=${visibleKeys}&expanded=${expandedKeys}&q=${encodeURIComponent(search.trim())}`,
    )
  }

  const toggleGroup = (key: RosterGroupKey) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }))

  const toggleShowAll = (key: RosterGroupKey, total: number) =>
    setVisibleCounts((prev) => ({ ...prev, [key]: prev[key] > GROUP_ROW_CAP ? GROUP_ROW_CAP : total }))

  const changeTooltip = `How this person's average score moved compared with the previous ${RANGE_PERIOD[timeRange]}.`
  const trendTooltip = `The shape of the score across the last ${RANGE_PERIOD[timeRange]}, so a steady slide looks different from a sudden drop.`

  const renderGroupCard = (group: (typeof GROUPS)[number], index: number) => {
    const groupRows = groups[group.key]
    const shownCount = visibleCounts[group.key]
    const isCollapsed = searching ? false : collapsed[group.key]
    const headerCluster = (
      <>
        <span className={classNames('d-flex align-items-center justify-content-center flex-shrink-0', styles.chevronSlot)}>
          <i className={classNames('bi zocks-text-gray-600', isCollapsed ? 'bi-chevron-down' : 'bi-chevron-up')} />
        </span>
        <span className={classNames('lead-small text-nowrap', group.headerClass)}>
          {group.title} · {groupRows.length}
        </span>
        <span className='paragraph-small zocks-text-gray-600 text-truncate'>{group.explanation(timeRange)}</span>
      </>
    )
    return (
      <div
        className={classNames(
          'border zocks-border-color-gray-200 zocks-border-0.75 zocks-bg-color-white overflow-hidden',
          skipEntrance ? null : styles.rowEnter,
        )}
        style={skipEntrance ? undefined : { animationDelay: `${index * 80}ms` }}
      >
        {isCollapsed ? (
          <button
            type='button'
            className={classNames('d-flex align-items-center gap-3 w-100 border-0 bg-transparent text-start px-3 zocks-py-0.75', styles.groupToggle)}
            aria-expanded={false}
            onClick={() => toggleGroup(group.key)}
          >
            {headerCluster}
          </button>
        ) : (
          <table className={classNames('table mb-0', styles.groupCardTable)}>
            <thead>
              <tr>
                <th>
                  <button
                    type='button'
                    className={classNames('d-flex align-items-center gap-3 border-0 bg-transparent text-start mw-0 p-0', styles.groupToggle)}
                    aria-expanded={true}
                    onClick={() => toggleGroup(group.key)}
                  >
                    {headerCluster}
                  </button>
                </th>
                <th className={classNames('text-end', styles.columnLabel, styles.numericCol)}>Meetings</th>
                <th className={classNames('text-end', styles.columnLabel, styles.numericCol)}>Score</th>
                <th className={classNames('text-end', styles.columnLabel, styles.numericCol)}>
                  <span className='insights-label-with-tooltip'>
                    Change
                    <InfoTooltip text={changeTooltip} />
                  </span>
                </th>
                <th className={classNames('text-start', styles.columnLabel, styles.numericCol)}>
                  <span className='insights-label-with-tooltip'>
                    Trend
                    <InfoTooltip text={trendTooltip} />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              {groupRows.slice(0, shownCount).map((row) => (
                <tr
                  key={row.id}
                  role='button'
                  tabIndex={0}
                  className={styles.memberRow}
                  onClick={() => openRow(row)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      openRow(row)
                    }
                  }}
                >
                  <td>
                    <div className='d-flex align-items-center gap-3'>
                      <UserAvatar useInitials initialLetters={getInitials(primaryLabel(row))} width='32px' height='32px' className='lead-small flex-shrink-0' />
                      <div className='d-flex flex-column mw-0'>
                        <span className='paragraph-medium zocks-text-gray-900 text-truncate'>{primaryLabel(row)}</span>
                        {hasName(row) ? <span className='paragraph-small zocks-text-gray-600 text-truncate'>{row.email}</span> : null}
                      </div>
                    </div>
                  </td>
                  <td className={classNames('paragraph-medium zocks-text-gray-600 text-end', styles.numericCol, styles.tabularNums)}>{row.meetingCount}</td>
                  <td
                    className={classNames(
                      'paragraph-large text-end',
                      styles.numericCol,
                      styles.tabularNums,
                      // A provisional score is not a judgement, so it carries none of the weight of a real one
                      row.score === null ? 'zocks-text-gray-600' : 'bold zocks-text-gray-900',
                    )}
                  >
                    {row.score !== null ? Math.round(row.score) : row.provisionalScore !== null ? Math.round(row.provisionalScore) : '–'}
                  </td>
                  <td className={classNames('text-end', styles.numericCol)}>{row.delta === null ? null : <ChangeChip delta={Math.round(row.delta)} />}</td>
                  <td className={classNames('text-start', styles.numericCol)}>
                    {row.score === null ? null : (
                      <div className='d-flex justify-content-start'>
                        <Sparkline values={row.history} delta={row.delta} />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!isCollapsed && groupRows.length > GROUP_ROW_CAP ? (
          <div className='d-flex px-3 zocks-py-0.75 border-top zocks-border-color-gray-200'>
            <button
              type='button'
              className='btn btn-link p-0 insights-link border-0 bg-transparent paragraph-small'
              onClick={() => toggleShowAll(group.key, groupRows.length)}
            >
              {shownCount > GROUP_ROW_CAP ? 'Show less' : `Show all ${groupRows.length}`}
            </button>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <Container fluid className='d-flex flex-column zocks-gap-3 p-0 w-100 full-size-content insights-view'>
      <div className='w-100'>
        <InsightsNavTabs active='coaching' />
      </div>
      <div className={classNames('w-100 zocks-px-2.0', styles.pageContent)}>
        <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap zocks-mb-3'>
          <div>
            <h1 className='h2'>Coaching</h1>
            <p className='insights-section-subtitle mb-0'>
              See how each person is scoring across the <span className='bold'>{RANGE_PERIOD_TEXT[timeRange]}</span> ·{' '}
              <WhatsCounted periodText={RANGE_PERIOD_TEXT[timeRange]} />
            </p>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <div
              className={classNames(
                'd-flex align-items-center zocks-gap-1.25 border rounded-3 zocks-border-color-gray-400 zocks-px-0.75 zocks-bg-color-white',
                styles.searchField,
              )}
            >
              <i className='bi bi-search zocks-text-gray-500' />
              <Form.Control
                type='search'
                autoComplete='off'
                value={search}
                onChange={(event) => {
                  const value = event.currentTarget.value
                  setSearch(value)
                  setSkipEntrance(true)
                  if (!value.trim()) {
                    setCollapsed(DEFAULT_COLLAPSED)
                  }
                }}
                disabled={loading}
                placeholder='Search users'
                aria-label='Search users'
                className='border-0 px-0 paragraph-small'
              />
            </div>
            <div className={loading ? 'opacity-50 pe-none' : undefined}>
              <SegmentedControl
                options={RANGE_OPTIONS}
                activeKey={timeRange}
                ariaLabel='Meeting scores time range'
                onSelect={(key) => {
                  if (key !== timeRange) {
                    setTimeRange(key as RangeKey)
                    setSkipEntrance(false)
                  }
                }}
              />
            </div>
          </div>
        </div>
        {loading ? (
          <div className={classNames('insights-initial-loading', styles.loadingPanel)}>
            <div className='insights-loading-spinner' />
            <div className='insights-loading-text'>Pulling up meeting scores...</div>
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className='paragraph-medium zocks-text-gray-600 text-center py-5'>
            {rows.length === 0 ? 'No active users yet' : 'No users match your search'}
          </div>
        ) : (
          <div key={timeRange} className='d-flex flex-column zocks-gap-1'>
            {visibleGroups.map((group, groupIndex) => (
              <Fragment key={group.key}>{renderGroupCard(group, groupIndex)}</Fragment>
            ))}
          </div>
        )}
      </div>
    </Container>
  )
}
